import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Lock, 
  Mail, 
  MessageSquare, 
  Users, 
  Trash2, 
  RefreshCw,
  LogOut,
  Calendar,
  Building,
  User,
  Home,
  Phone,
  Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('contacts');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (data.success) {
        const auth = btoa(`${credentials.username}:${credentials.password}`);
        setAuthHeader(`Basic ${auth}`);
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', `Basic ${auth}`);
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setCredentials({ username: '', password: '' });
    setContacts([]);
    setNewsletters([]);
    localStorage.removeItem('adminAuth');
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { 'Authorization': authHeader }
      });
      const data = await response.json();
      if (data.success) {
        setContacts(data.contacts);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsletters = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletters`, {
        headers: { 'Authorization': authHeader }
      });
      const data = await response.json();
      if (data.success) {
        setNewsletters(data.newsletters);
      }
    } catch (err) {
      console.error('Failed to fetch newsletters:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this contact submission?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/contact/${submissionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });
      const data = await response.json();
      if (data.success) {
        setContacts(contacts.filter(c => c.submissionId !== submissionId));
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const deleteNewsletter = async (email) => {
    if (!window.confirm('Are you sure you want to delete this newsletter subscription?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });
      const data = await response.json();
      if (data.success) {
        setNewsletters(newsletters.filter(n => n.email !== email));
      }
    } catch (err) {
      console.error('Failed to delete newsletter:', err);
    }
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      setAuthHeader(savedAuth);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && authHeader) {
      fetchContacts();
      fetchNewsletters();
    }
  }, [isAuthenticated, authHeader]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">Admin Dashboard</CardTitle>
            <CardDescription>Please login to access the admin panel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Email</Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="Enter your email"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              {error && (
                <div className="text-red-500 text-sm text-center" data-testid="admin-error-message">
                  {error}
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
                disabled={loading}
                data-testid="admin-login-button"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link 
                to="/" 
                className="text-blue-600 hover:text-blue-800 flex items-center justify-center gap-2 transition-colors"
                data-testid="return-home-link"
              >
                <Home className="w-4 h-4" />
                Return to Homepage
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-blue-100">Manage your contact submissions and newsletters</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-blue-600"
                  data-testid="return-home-button"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Homepage
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-blue-600"
                onClick={handleLogout}
                data-testid="admin-logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total Contact Messages</p>
                  <p className="text-3xl font-bold text-gray-900">{contacts.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Newsletter Subscribers</p>
                  <p className="text-3xl font-bold text-gray-900">{newsletters.length}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center mb-6">
            <TabsList>
              <TabsTrigger value="contacts" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Contact Messages
              </TabsTrigger>
              <TabsTrigger value="newsletters" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Newsletter Subscribers
              </TabsTrigger>
            </TabsList>
            <Button 
              variant="outline" 
              onClick={() => activeTab === 'contacts' ? fetchContacts() : fetchNewsletters()}
              disabled={loading}
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <TabsContent value="contacts">
            {contacts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No contact submissions yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact, index) => (
                  <Card key={contact.submissionId || index} className="shadow-md hover:shadow-lg transition-shadow" data-testid={`contact-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {contact.firstName} {contact.lastName}
                            </h3>
                            <p className="text-sm text-gray-500">{contact.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(contact.submittedAt || contact.submissionDate)}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteContact(contact.submissionId)}
                            data-testid={`delete-contact-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Contact Details Grid */}
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4 text-blue-500" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                        {contact.company && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Building className="w-4 h-4 text-blue-500" />
                            <span>{contact.company}</span>
                          </div>
                        )}
                        {contact.companyType && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Briefcase className="w-4 h-4 text-teal-500" />
                            <span className="capitalize">{contact.companyType.replace('-', ' ')}</span>
                          </div>
                        )}
                        {contact.serviceInterest && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MessageSquare className="w-4 h-4 text-teal-500" />
                            <span className="capitalize">{contact.serviceInterest.replace(/-/g, ' ')}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Service Interest Badge */}
                      {contact.serviceInterest && (
                        <div className="mb-3">
                          <Badge className="bg-blue-100 text-blue-700 capitalize">
                            {contact.serviceInterest.replace(/-/g, ' ')}
                          </Badge>
                          {contact.status && (
                            <Badge className={`ml-2 ${contact.status === 'new' ? 'bg-green-100 text-green-700' : contact.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                              {contact.status}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Message */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-500 mb-2">Message:</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="newsletters">
            {newsletters.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No newsletter subscribers yet</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-md">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-4 px-6 font-semibold text-gray-600">Email</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-600">Subscribed Date</th>
                        <th className="text-right py-4 px-6 font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletters.map((newsletter, index) => (
                        <tr key={newsletter.email} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`newsletter-row-${index}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                <Mail className="w-4 h-4 text-teal-600" />
                              </div>
                              {newsletter.email}
                            </div>
                          </td>
                          <td className="py-4 px-6 text-gray-500">
                            {formatDate(newsletter.subscribedAt)}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteNewsletter(newsletter.email)}
                              data-testid={`delete-newsletter-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
